import { AppShell, Group, Text } from "@mantine/core"
import { useLocalStorage } from "@mantine/hooks"
import type { ReactNode } from "react"

import { CivalaMark } from "../CivalaMark"
import { CivalaWordmark } from "../CivalaWordmark"
import { AppSidebar, type AppSidebarSection } from "./AppSidebar"
import { AppTopBar, type AppTopBarProps } from "./AppTopBar"

export type AppShellLayoutBrand = {
  /**
   * Icon rendered in the sidebar brand area, and the only brand element left
   * when the sidebar collapses. Defaults to the standard `<CivalaMark />`;
   * override only for a genuinely different mark.
   */
  readonly icon?: ReactNode
  /**
   * Secondary brand text (the app name — "Investor", "Nexus"). Hidden when
   * collapsed. The "Civala" wordmark beside it is not a prop: it is the logo
   * asset and is identical across apps.
   */
  readonly subtitle: string
}

export type AppShellLayoutProps = {
  /** Unique key used to persist sidebar collapse state in localStorage. */
  readonly persistKey: string
  /** Top-level nav sections for the sidebar. */
  readonly sidebarSections: ReadonlyArray<AppSidebarSection>
  /** Current router pathname — used to highlight the active section. */
  readonly activePath: string
  /** Navigate callback — TanStack Router or any other router. */
  readonly onNavigate: (path: string) => void
  /** Brand block rendered at the top of the sidebar; clicking it toggles collapse. */
  readonly brand: AppShellLayoutBrand
  /** Slots for the top bar (left/center/right). */
  readonly topBar: AppTopBarProps
  /** Routed content. */
  readonly children: ReactNode
  /** Optional view-transition-name for the main content area. */
  readonly viewTransitionName?: string
  /**
   * When true, the sidebar runs in accordion mode: only the active section's
   * children are shown (others collapse to a header). Default off — opt-in per app.
   */
  readonly sidebarAccordion?: boolean
}

/**
 * AppShellLayout — the canonical Civala chrome layout.
 *
 * Composes Mantine `AppShell` + the prop-driven `AppSidebar` and `AppTopBar`.
 * Owns the collapse state (persisted to localStorage under `persistKey`).
 *
 * Adds the `.civala-chrome-pitch` class so that scheme-aware tokens in
 * `pitch-global.css` apply: white-on-light header + off-white sidebar in
 * light mode, navy in dark mode.
 *
 * Each consuming app passes its own nav sections, navigate callback, and
 * top-bar content. No app-specific behavior lives in this component.
 */
export function AppShellLayout({
  persistKey,
  sidebarSections,
  activePath,
  onNavigate,
  brand,
  topBar,
  children,
  viewTransitionName,
  sidebarAccordion = false,
}: AppShellLayoutProps) {
  const [collapsed, setCollapsed] = useLocalStorage<boolean>({
    key: `civala.sidebar.collapsed.${persistKey}`,
    defaultValue: false,
    // Read synchronously on first render so the sidebar doesn't flash from
    // expanded → collapsed (or vice versa) when the user has a persisted
    // preference. Safe in SPAs; would need reconsideration if we add SSR.
    getInitialValueInEffect: false,
  })

  const toggleCollapsed = () => setCollapsed((c) => !c)

  return (
    <AppShell
      className="civala-chrome-pitch"
      header={{ height: 70 }}
      navbar={{
        width: collapsed ? 56 : 240,
        breakpoint: "sm",
      }}
      padding="md"
      layout="alt"
      styles={{
        main: {
          background: "var(--pitch-chrome-main-bg)",
          ...(viewTransitionName ? { viewTransitionName } : {}),
        },
      }}
    >
      <AppShell.Navbar p={0}>
        <Group
          h={70}
          px="md"
          gap={10}
          align="center"
          wrap="nowrap"
          style={{
            borderBottom: "1px solid var(--pitch-chrome-sidebar-border)",
            cursor: "pointer",
          }}
          onClick={toggleCollapsed}
        >
          {brand.icon ?? <CivalaMark size={20} ringColor="var(--pitch-chrome-sidebar-brand-text)" />}
          {!collapsed && (
            <>
              <CivalaWordmark height={19} variant="auto" />
              <Text fz={20} fw={400} c="var(--pitch-chrome-sidebar-brand-text-muted)" style={{ lineHeight: 1 }}>
                {brand.subtitle}
              </Text>
            </>
          )}
        </Group>
        <AppSidebar
          sections={sidebarSections}
          collapsed={collapsed}
          activePath={activePath}
          onNavigate={onNavigate}
          accordion={sidebarAccordion}
        />
      </AppShell.Navbar>

      <AppShell.Header>
        <AppTopBar left={topBar.left} center={topBar.center} right={topBar.right} />
      </AppShell.Header>

      <AppShell.Main>{children}</AppShell.Main>
    </AppShell>
  )
}
